import { X } from "lucide-react";
import { type ReactNode, useEffect } from "react";
import { z } from "zod";

import { STATUS_LABEL_KEYS } from "@/modules/dashboard/payments-activity/constants/paymentsActivity.constants";
import { OrganizationScopePicker } from "@/modules/dashboard/payments-config/components/OrganizationScopePicker";
import { useOrganizationsCatalog } from "@/modules/dashboard/payments-config/hooks/useOrganizationsCatalog";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAmountInputControlled } from "@/shared/components/forms/ColocaAmountInput";
import { ColocaDateRangeField } from "@/shared/components/forms/ColocaDateRangeField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatRail } from "@/shared/utils/railDisplay";

import type { LedgerDirection, MovementIncidentFilter } from "../../types/payments-wallets.types";
import {
  activePreset,
  EMPTY_MOVEMENTS_FILTERS,
  MOVEMENT_INCIDENT_FILTERS,
  MOVEMENT_OPERATION_STATUSES,
  MOVEMENT_PRESETS,
  MOVEMENT_RAILS,
  MOVEMENTS_SEARCH_MAX,
  type MovementPreset,
  type WalletMovementsFilters as Filters,
} from "../../utils/walletMovements";

const ALL = "__all__";
const PRESETS = Object.keys(MOVEMENT_PRESETS) as MovementPreset[];

const rangeSchema = z.object({ from: z.string(), to: z.string() });

export interface MovementsFiltersProps {
  filters: Filters;
  onChange: (patch: Partial<Filters>) => void;
  onReplace: (next: Filters) => void;
  onClear: () => void;
  hasActiveFilters: boolean;
  currencies: string[];
}

/**
 * How staff find the payment a client reports: a one-click view for the usual cases, then the organization,
 * the amount (what reached the beneficiary works), the days, the rail and the state of the operation and of
 * its incident.
 */
export function MovementsFilters({
  filters,
  onChange,
  onReplace,
  onClear,
  hasActiveFilters,
  currencies,
}: MovementsFiltersProps) {
  const { t } = useTranslations("wallets");
  const { t: tBatches } = useTranslations("payoutBatches");
  const { t: tActivity } = useTranslations("paymentsActivity");
  const { t: tIncidents } = useTranslations("paymentIncidents");
  const catalog = useOrganizationsCatalog();
  const preset = activePreset(filters);
  const range = useZodForm(rangeSchema, { from: filters.from, to: filters.to });

  // The day range is the one RHF-bound control (there is no controlled range picker); it reports upstream.
  useEffect(() => {
    const subscription = range.watch((value) => onChange({ from: value.from ?? "", to: value.to ?? "" }));
    return () => subscription.unsubscribe();
  }, [range, onChange]);
  // Keep the picker in step when a preset or "clear" changes the days from outside.
  useEffect(() => {
    const current = range.getValues();
    if (current.from !== filters.from || current.to !== filters.to) {
      range.reset({ from: filters.from, to: filters.to });
    }
  }, [filters.from, filters.to, range]);

  const currencyCodes =
    filters.currency && !currencies.includes(filters.currency)
      ? [filters.currency, ...currencies]
      : currencies;
  const statusLabel = (status: string) => {
    const key = STATUS_LABEL_KEYS[status];
    return key ? tActivity(key) : status;
  };

  return (
    <div className="space-y-3">
      <div
        className="flex flex-wrap items-center gap-2"
        role="group"
        aria-label={t("movements_presets_label")}
      >
        {PRESETS.map((name) => (
          <ColocaButton
            key={name}
            type="button"
            size="sm"
            variant={preset === name ? "primary" : "secondary"}
            aria-pressed={preset === name}
            onClick={() =>
              onReplace(
                preset === name
                  ? EMPTY_MOVEMENTS_FILTERS
                  : { ...EMPTY_MOVEMENTS_FILTERS, ...MOVEMENT_PRESETS[name] },
              )
            }
          >
            {t(`movements_preset_${name}`)}
          </ColocaButton>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1.2fr)_repeat(2,minmax(0,1fr))]">
        <Field
          label={t("movements_filter_search")}
          htmlFor="movements-search"
          className="col-span-2 xl:col-span-1"
        >
          <ColocaSearchInput
            id="movements-search"
            value={filters.search}
            onValueChange={(search) => onChange({ search })}
            placeholder={t("movements_filter_search_placeholder")}
            maxLength={MOVEMENTS_SEARCH_MAX}
            clearable
          />
        </Field>
        <OrganizationScopePicker
          id="movements-organization"
          className="col-span-2 xl:col-span-1"
          value={filters.organizationId}
          onValueChange={(organizationId) => onChange({ organizationId })}
          catalog={catalog}
          labels={{
            label: t("movements_filter_organization"),
            placeholder: tBatches("org_picker_placeholder"),
            loading: tBatches("org_picker_loading"),
            empty: tBatches("org_picker_empty"),
            searching: tBatches("org_picker_searching"),
            searchError: tBatches("org_picker_search_error"),
            catalogError: tBatches("org_picker_catalog_error"),
            catalogTruncated: tBatches("org_picker_catalog_truncated"),
            globalOption: t("movements_filter_organization_all"),
            globalHint: t("movements_filter_organization_all_hint"),
            orgHint: t("movements_filter_organization_hint"),
          }}
        />
        <Field label={t("movements_filter_min")} htmlFor="movements-min">
          <ColocaAmountInputControlled
            id="movements-min"
            value={filters.minAmount}
            onValueChange={(minAmount) => onChange({ minAmount })}
            suffix={filters.currency ?? undefined}
            maxFractionDigits={2}
            groupThousands
          />
        </Field>
        <Field label={t("movements_filter_max")} htmlFor="movements-max">
          <ColocaAmountInputControlled
            id="movements-max"
            value={filters.maxAmount}
            onValueChange={(maxAmount) => onChange({ maxAmount })}
            suffix={filters.currency ?? undefined}
            maxFractionDigits={2}
            groupThousands
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-[minmax(0,1.4fr)_repeat(5,minmax(0,1fr))]">
        <Field
          label={t("movements_filter_days")}
          htmlFor="movements-days"
          className="col-span-2 xl:col-span-1"
        >
          <ColocaForm
            form={range}
            onSubmit={() => {
              // Live-applied through `watch`; the form only keeps Enter from reloading the page.
            }}
            className="space-y-0"
          >
            <ColocaDateRangeField
              nameFrom="from"
              nameTo="to"
              placeholder={t("movements_filter_days_placeholder")}
              maxDate={new Date()}
            />
          </ColocaForm>
        </Field>
        <SelectField
          id="movements-currency"
          label={t("filter_currency_label")}
          value={filters.currency}
          allLabel={t("filter_currency_all")}
          options={currencyCodes.map((code) => ({ value: code, label: code }))}
          onChange={(currency) => onChange({ currency })}
        />
        <SelectField
          id="movements-rail"
          label={t("movements_filter_rail")}
          value={filters.rail}
          allLabel={t("movements_filter_rail_all")}
          options={MOVEMENT_RAILS.map((rail) => ({ value: rail, label: formatRail(rail) }))}
          onChange={(rail) => onChange({ rail })}
        />
        <SelectField
          id="movements-direction"
          label={t("movements_filter_direction")}
          value={filters.direction}
          allLabel={t("statement_filter_dir_all")}
          options={[
            { value: "D", label: t("statement_filter_dir_debit") },
            { value: "C", label: t("statement_filter_dir_credit") },
          ]}
          onChange={(direction) => onChange({ direction: direction as LedgerDirection | null })}
        />
        <SelectField
          id="movements-status"
          label={t("movements_filter_status")}
          value={filters.operationStatus}
          allLabel={t("movements_filter_status_all")}
          options={MOVEMENT_OPERATION_STATUSES.map((status) => ({
            value: status,
            label: statusLabel(status),
          }))}
          onChange={(operationStatus) => onChange({ operationStatus })}
        />
        <SelectField
          id="movements-incident"
          label={t("movements_filter_incident")}
          value={filters.incident}
          allLabel={t("movements_filter_incident_all")}
          options={MOVEMENT_INCIDENT_FILTERS.map((value) => ({
            value,
            label: tIncidents(`filter_${value}`, { provider: tIncidents("provider_fallback") }),
          }))}
          onChange={(incident) => onChange({ incident: incident as MovementIncidentFilter | null })}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{t("movements_filter_amount_hint")}</p>
        {hasActiveFilters && (
          <ColocaButton variant="ghost" size="sm" icon={X} onClick={onClear}>
            {t("filter_clear")}
          </ColocaButton>
        )}
      </div>
    </div>
  );
}

function SelectField({
  id,
  label,
  value,
  allLabel,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string | null;
  allLabel: string;
  options: { value: string; label: string }[];
  onChange: (value: string | null) => void;
}) {
  return (
    <Field label={label} htmlFor={id}>
      <ColocaSelect
        id={id}
        value={value ?? ALL}
        onValueChange={(next) => onChange(next === ALL ? null : next)}
        options={[{ value: ALL, label: allLabel }, ...options]}
      />
    </Field>
  );
}

function Field({
  label,
  htmlFor,
  children,
  className,
}: {
  label: string;
  htmlFor: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      <label
        htmlFor={htmlFor}
        className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase"
      >
        {label}
      </label>
      {children}
    </div>
  );
}
